import { DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { Component } from '@angular/core';
import { LayoutService } from '../../service/app.layout.service';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ContextService } from 'src/app/services/utils/context.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppMenuitemComponent } from './app.menuitem.component';
import { MenuItem } from 'primeng/api';
import { TOOL_MODULES, visibleToolModules } from 'src/app/utils/tool-navigation';
import { USER_MANUALS } from 'src/app/utils/user-manuals';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  standalone: true,
  imports: [AppMenuitemComponent]
})
/**
 * Componente responsable de construir y renderizar el menú lateral de la aplicación.
 *
 * Propósito:
 * - Adaptar la navegación según el contexto funcional seleccionado (TMS, Warehouse, etc.).
 * - Centralizar el mapeo entre opciones de menú y rutas disponibles.
 *
 * Flujo general:
 * 1. Escucha cambios de ruta y de `pageMode` desde `ContextService`.
 * 2. Reconstruye el modelo de menú con `actualizarMenu()`.
 * 3. Expone el modelo al template para su renderizado dinámico.
 */
export class AppMenuComponent implements OnInit, OnDestroy {
  model: any[] = [];

  constructor(public layoutService: LayoutService) {}

  private readonly router = inject(Router);

  private readonly contextService = inject(ContextService);

  destroyRef = inject(DestroyRef);

  pageModes: any[] = [
    { name: 'TMS', key: 'TMS' },
    { name: 'IDP', key: 'IDP' },
    { name: 'Warehouse', key: 'Warehouse' },
    { name: 'Custom Service', key: 'Custom Service' },
    { name: 'External Data', key: 'External Data' },
    { name: 'Inventory', key: 'Inventory' },
    { name: 'Expenses', key: 'Expenses' },
    { name: 'Administration', key: 'Administration' }
  ];

  selectedPageMode: string = null;

  pageModeState: string = null;

  usuario: any = JSON.parse(localStorage.getItem('usuario') || '{}');

  url: string = null;

  API_URL: string = environment.API_URL;

  public subs: Subscription[] = [];

  endpoint: string = null;

  miInputFile = null;

  excel: string | ArrayBuffer = null;

  processing: boolean = false;

  rootUrl: string = window.location.origin;

  ngOnInit() {
    console.log('rootUrl => ', this.rootUrl);

    this.url = this.router.url;
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.url = event['url'];
      });

    this.contextService.pageMode.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pageMode) => {
      console.log('pageMode => ', pageMode);
      this.pageModeState = pageMode;
      this.actualizarMenu();
    });
  }

  /**
   * Construye dinámicamente la estructura del menú según el modo de página activo.
   *
   * @returns void
   *
   * Flujo:
   * 1. Evalúa `pageModeState` para decidir qué bloque de navegación mostrar.
   * 2. Construye `restOfMenu` con rutas y opciones permitidas por contexto.
   * 3. Actualiza `model` solo cuando cambia la raíz del menú para evitar renders innecesarios.
   */
  actualizarMenu() {
    let modelClone = [];

    let restOfMenu: MenuItem[] = [];

    if (this.pageModeState == 'TMS') {
      restOfMenu.push({
        label: 'TMS',
        icon: 'pi pi-fw pi-eject',
        items: [
          // {
          //   label: 'Menu',
          //   icon: 'pi pi-fw pi-ellipsis-v',
          //   routerLink: ['/customer-services-menu'],
          // },
          {
            label: 'Pending Options',
            icon: 'pi pi-fw pi-clock',
            items: [
              {
                label: 'Pending',
                routerLink: ['/tms/pendings']
              },
              {
                label: 'Processed Pendings',
                routerLink: ['/tms/processeds']
              },
              {
                label: 'Cancelled Pendings',
                routerLink: ['/tms/cancelled-pendings']
              }
            ]
          },
          {
            label: 'Preparation Options',
            icon: 'pi pi-fw pi-box',
            items: [
              {
                label: 'Preparations',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/tms/preparations']
              },
              {
                label: 'Sug Preparations',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/tms/sug-preparations']
              },
              {
                label: 'Special Sug Preparations',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/tms/special-sug-preparations']
              },
              {
                label: 'Sug Mix Pallets',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/tms/sug-mix-pallets']
              },
              // {
              //   label: 'US Trucks Preparations',
              //   icon: 'pi pi-fw pi-truck',
              //   routerLink: ['/tms/us-truck-preparations']
              // },

              // {
              //   label: 'Truck Plans',
              //   icon: 'pi pi-fw pi-truck',
              //   routerLink: ['/tms/truck-plans']
              // },

              // {
              //   label: 'Build Truck',
              //   icon: 'pi pi-fw pi-cog',
              //   routerLink: ['/tms/sug-preparations'],
              // },
            ]
          },
          {
            label: 'Inbound',
            icon: 'pi pi-fw pi-send',
            items: [
              {
                label: 'Inbound Tracker',
                icon: 'pi pi-fw pi-list',
                routerLink: ['/tms/inbound-tracker']
              },
              {
                label: 'Inbound Preparations',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/tms/inbound-preparations']
              },
              {
                label: 'Back Order Upload',
                icon: 'pi pi-fw pi-upload',
                routerLink: ['/tms/back-order/upload']
              },
              {
                label: 'Back Order List',
                icon: 'pi pi-fw pi-list',
                routerLink: ['/tms/back-order/list']
              }
            ]
          },
          {
            label: 'Shipments',
            icon: 'pi pi-fw pi-send',
            items: [
              {
                label: 'List Shipments',
                icon: 'pi pi-fw pi-list',
                routerLink: ['/tms/shipments']
              },
              {
                label: 'Generate Shipments',
                icon: 'pi pi-fw pi-plus-circle',
                routerLink: ['/tms/shipments-generate']
              }
            ]
          },
          {
            label: 'Requirements',
            icon: 'pi pi-fw pi-history',
            items: [
              {
                label: 'List',
                icon: 'pi pi-fw pi-history',
                routerLink: ['/tms/requirements']
              },
              {
                label: 'Files',
                icon: 'pi pi-file-excel',
                routerLink: ['/tms/groups-requirements']
              },
              {
                label: 'Quantity Changes',
                icon: 'pi pi-fw pi-list',
                routerLink: ['/tms/requirements-quantity-changes']
              },
              {
                label: 'Upload',
                icon: 'pi pi-file-excel',
                routerLink: ['/tms/load-requirements']
              }
            ]
          },
          // {
          //   label: 'KPI',
          //   icon: 'pi pi-fw pi-chart-bar',
          //   items: [
          //     {
          //       label: 'Occupacy KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/tms/kpi/occupacy-kpi']
          //     },
          //     {
          //       label: 'Total Cost KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/tms/kpi/total-cost-kpi']
          //     },
          //     {
          //       label: 'Total CO2 KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/tms/kpi/total-co2-kpi']
          //     },
          //     {
          //       label: 'Lower SKU Quantity',
          //       icon: 'pi pi-fw pi-list',
          //       items: [
          //         {
          //           label: "SKU's lower than Pallet Quantity",
          //           icon: 'pi pi-fw pi-list',
          //           routerLink: ['/tms/kpi/lower-pallet-qty']
          //         },
          //         {
          //           label: "SKU's lower than one Layer",
          //           icon: 'pi pi-fw pi-list',
          //           routerLink: ['/tms/kpi/lower-layer-qty']
          //         },
          //         {
          //           label: "SKU's lower than one Shipper",
          //           icon: 'pi pi-fw pi-list',
          //           routerLink: ['/tms/kpi/lower-shipper-qty']
          //         }
          //       ]
          //     }
          //   ]
          // },
          {
            label: 'Settings',
            icon: 'pi pi-fw pi-cog',
            routerLink: '/tms/settings',
            command: () => {
              console.log(this.url);
              sessionStorage.setItem('lastUrl', this.url);
              // this.router.navigate(['/tms/settings']);
            }
          }
        ]
      });
    } else if (this.pageModeState == 'IDP') {
      restOfMenu.push({
        label: 'IDP',
        icon: 'pi pi-fw pi-map',
        items: [
          {
            label: 'Suggestions by next doh',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/shipment-consolidation-suggestions']
          },
          {
            label: 'Suggestions by next doh one',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/shipment-consolidation-suggestions-one']
          },
          {
            label: 'Suggestion by annual quantity shipped.',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/shipment-frequency-recommendations']
          },
          {
            label: 'Weekly Summaries',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/requirements-weekly-summaries']
          },
          {
            label: 'Material Summary',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/material-requirements-summary']
          },
          {
            label: 'Processed Summary',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/material-processed-summary']
          },
          {
            label: 'Less than a Shipper',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/idp/requirements-quantity-changes']
          },
          // {
          //   label: 'Requirements',
          //   icon: 'pi pi-fw pi-history',
          //   items: [
          //     {
          //       label: 'List',
          //       icon: 'pi pi-fw pi-history',
          //       routerLink: ['/idp/requirements']
          //     },
          //     {
          //       label: 'Files',
          //       icon: 'pi pi-file-excel',
          //       routerLink: ['/idp/group-requirements']
          //     },
          //     {
          //       label: 'Quantity Changes',
          //       icon: 'pi pi-fw pi-list',
          //       routerLink: ['/idp/requirements-quantity-changes']
          //     }
          //   ]
          // }
        ]
      });
    } else if (this.pageModeState == 'Customer Services') {
      restOfMenu.push({
        label: 'Customer Services',
        icon: 'pi pi-fw pi-eject',
        items: [
          // {
          //   label: 'Menu',
          //   icon: 'pi pi-fw pi-ellipsis-v',
          //   routerLink: ['/customer-services-menu'],
          // },
          {
            label: 'Pending Options',
            icon: 'pi pi-fw pi-clock',
            items: [
              {
                label: 'Pending',
                routerLink: ['/customer-services/pendings']
              },
              {
                label: 'Processed Pendings',
                routerLink: ['/customer-services/processeds']
              },
              {
                label: 'Cancelled Pendings',
                routerLink: ['/customer-services/cancelled-pendings']
              }
            ]
          },
          {
            label: 'Delivery Order Options',
            icon: 'pi pi-fw pi-box',
            items: [
              {
                label: 'Delivery Orders',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/customer-services/preparations']
              },
              {
                label: 'Sug Delivery Orders',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/customer-services/sug-preparations']
              },
              {
                label: 'Sug Mix Pallets',
                icon: 'pi pi-fw pi-box',
                routerLink: ['/customer-services/sug-mix-pallets']
              }

              // {
              //   label: 'Build Truck',
              //   icon: 'pi pi-fw pi-cog',
              //   routerLink: ['/customer-services/sug-preparations'],
              // },
            ]
          },
          {
            label: 'Sales Orders',
            icon: 'pi pi-fw pi-history',
            items: [
              {
                label: 'Sales Orders',
                icon: 'pi pi-file-excel',
                routerLink: ['/customer-services/groups-requirements']
              },
              {
                label: 'Sales Order Items',
                icon: 'pi pi-fw pi-history',
                routerLink: ['/customer-services/requirements']
              },
              {
                label: 'Upload Sales Orders',
                icon: 'pi pi-file-excel',
                routerLink: ['/customer-services/load-requirements']
              }
            ]
          },
          // {
          //   label: 'KPI',
          //   icon: 'pi pi-fw pi-chart-bar',
          //   items: [
          //     {
          //       label: 'Occupacy KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/customer-services/kpi/occupacy-kpi']
          //     },
          //     {
          //       label: 'Total Cost KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/customer-services/kpi/total-cost-kpi']
          //     },
          //     {
          //       label: 'Total CO2 KPI',
          //       icon: 'pi pi-fw pi-chart-bar',
          //       routerLink: ['/customer-services/kpi/total-co2-kpi']
          //     }
          //   ]
          // },
          {
            label: 'Clients',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/customer-services/clients']
          },
          {
            label: 'Settings',
            icon: 'pi pi-fw pi-cog',
            routerLink: '/customer-services/settings',
            command: () => {
              console.log(this.url);
              sessionStorage.setItem('lastUrl', this.url);
              // this.router.navigate(['/customer-services/settings']);
            }
          }
        ]
      });
    } else if (this.pageModeState == 'Warehouse') {
      restOfMenu.push({
        label: 'Logistics Warehouse Operations',
        icon: 'pi pi-fw pi-chart-bar',
        items: [
          // {
          //   label: 'Menu',
          //   icon: 'pi pi-fw pi-ellipsis-v',
          //   routerLink: ['/warehouse-menu'],
          // },
          {
            label: 'Raw',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/warehouse/raws']
          },
          {
            label: 'Raw Charts',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/warehouse/raws/charts']
          },
          {
            label: 'All Raw Charts',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/warehouse/raws/all-charts']
          },
          {
            label: 'KPI',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/warehouse/kpis']
          },
          {
            label: 'Meet Action Rails',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/warehouse/meeting-action-rails']
          }
          // {
          //   label: 'Meet Action Rail Charts',
          //   icon: 'pi pi-fw pi-chart-bar',
          //   routerLink: ['/warehouse/meeting-action-rails/charts'],
          // },
        ]
      });
    } else if (this.pageModeState == 'Custom Services') {
      restOfMenu.push({
        label: 'Custom Services',
        icon: 'pi pi-fw pi-chart-bar',
        items: [
          {
            label: 'Menu',
            icon: 'pi pi-fw pi-ellipsis-v',
            routerLink: ['/custom-services-menu']
          },
          {
            label: 'Raw',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/custom-services/raws']
          },
          {
            label: 'Raw Charts',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/custom-services/raws/charts']
          },
          {
            label: 'All Raw Charts',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/custom-services/raws/all-charts']
          },
          {
            label: 'KPI',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/custom-services/kpis']
          },
          {
            label: 'Meet Action Rails',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/custom-services/meeting-action-rails']
          }
          // {
          //   label: 'Meet Action Rail Charts',
          //   icon: 'pi pi-fw pi-chart-bar',
          //   routerLink: ['/warehouse/meeting-action-rails/charts'],
          // },
        ]
      });
    } else if (this.pageModeState == 'External Data') {
      restOfMenu.push({
        label: 'Masterdata',
        icon: 'pi pi-fw pi-chart-bar',
        items: [
          // {
          //   label: 'Menu',
          //   icon: 'pi pi-fw pi-ellipsis-v',
          //   routerLink: ['/external-data-menu'],
          // },
          // {
          //   label: 'Load Data Conf',
          //   icon: 'pi  pi-file-excel',
          //   routerLink: ['external-data/load-data-conf'],
          // },
          // {
          //   label: 'Load SKU Query',
          //   icon: 'pi  pi-file-excel',
          //   routerLink: ['external-data/load-sku-query'],
          // },
          {
            label: 'Containers',
            icon: 'pi pi-fw pi-box',
            routerLink: ['/external-data/containers']
          },
          {
            label: 'DG Days',
            icon: 'pi pi-fw pi-clock',
            routerLink: ['/external-data/dg-days']
          },
          {
            label: 'Pack Config',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/external-data/masterdatas']
          },
          {
            label: 'Pack Config Diff',
            icon: 'pi pi-fw pi-arrow-right-arrow-left',
            routerLink: ['/external-data/masterdata-merge']
          },
          {
            label: 'Pack Config Missing',
            icon: 'pi pi-fw pi-exclamation-triangle',
            routerLink: ['/external-data/masterdata-missing']
          },
          {
            label: 'Pack Config Check',
            icon: 'pi pi-fw pi-check-square',
            routerLink: ['/external-data/pack-config-check']
          },
          {
            label: 'DG List',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/external-data/dg-list']
          },
          {
            label: 'Pack Config Files Uploader',
            icon: 'pi pi-fw pi-upload',
            routerLink: ['/external-data/masterdata-files-uploader']
          },
          {
            label: 'MRP / Hyperion',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/external-data/mrp-hyperion']
          },
          {
            label: 'Transit',
            icon: 'pi pi-fw pi-truck',
            routerLink: ['/external-data/transits']
          },
          {
            label: 'ASP ABC',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/external-data/asp-abcs']
          },
          {
            label: 'Freight Costs',
            icon: 'pi pi-fw pi-dollar',
            routerLink: ['/external-data/freight-costs']
          },
          {
            label: 'Warehouses',
            icon: 'pi pi-fw pi-building',
            routerLink: ['/external-data/warehouses']
          },
          {
            label: 'Reset Data',
            icon: 'pi pi-fw pi-replay',
            routerLink: ['/external-data/reset-data']
          }
        ]
      });
    } else if (this.pageModeState == 'Inventory') {
      restOfMenu.push({
        label: 'Inventory',
        items: [
          {
            label: 'Home',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/']
          },
          {
            label: 'Inventory',
            icon: 'pi pi-fw pi-th-large',
            routerLink: ['/inventory']
          },
          {
            label: 'Prices',
            icon: 'pi pi-fw pi-dollar',
            routerLink: ['/inventory/prices']
          },
          {
            label: 'Uploaded files',
            icon: 'pi pi-fw pi-upload',
            routerLink: ['/inventory/uploaded-files']
          },
          {
            label: 'Discrepancies',
            icon: 'pi pi-fw pi-exclamation-triangle',
            routerLink: ['/inventory/discrepancies']
          },
          {
            label: 'Inventory KPI',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/inventory/kpi']
          }
        ]
      });
    } else if (this.pageModeState == 'Test tracking') {
      restOfMenu.push({
        label: 'Test tracking',
        items: [
          {
            label: 'Home',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/']
          },
          {
            label: 'Projects',
            icon: 'pi pi-fw pi-check-square',
            routerLink: ['/test-tracking']
          },
          {
            label: 'New project',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/test-tracking/projects/new']
          }
        ]
      });
    } else if (this.pageModeState == 'Expenses') {
      restOfMenu.push({
        label: 'Expenses',
        items: [
          {
            label: 'Home',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/']
          },
          {
            label: 'Expenses',
            icon: 'pi pi-fw pi-wallet',
            routerLink: ['/expenses']
          },
          {
            label: 'Create Expense',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/expenses/create']
          }
        ]
      });
    } else if (this.pageModeState == 'Administration') {
      restOfMenu.push({
        label: 'Administration',
        icon: 'pi pi-fw pi-user',
        items: [
          {
            label: 'Users',
            icon: 'pi pi-fw pi-user',
            routerLink: ['/administration/users']
          }
        ]
      });
    } else if (this.pageModeState == 'Authorization Requests') {
      let authorizationRequestsMenu: MenuItem = {
        label: 'Authorization Requests',
        icon: 'pi pi-fw pi-user',
        items: [
          {
            label: 'My Requests',
            icon: 'pi pi-fw pi-file-edit',
            routerLink: ['/authorization-requests/requests']
          }
        ]
      };

      if (this.usuario.roles.find((role: any) => role.name === 'ROLE_AR_AUTHORIZER')) {
        authorizationRequestsMenu.items.push({
          label: 'Requests to Approve',
          icon: 'pi pi-fw pi-verified',
          routerLink: ['/authorization-requests/authorizer-panel']
        });
        authorizationRequestsMenu.items.push({
          label: 'Edit My Authorizer Information',
          icon: 'pi pi-fw pi-user-edit',
          routerLink: ['/authorization-requests/authorizer-panel/edit']
        });
      }

      if (this.usuario.roles.find((role: any) => role.name === 'ROLE_AR_ADMIN' || role.name === 'ROLE_ADMIN')) {
        authorizationRequestsMenu.items.push({
          label: 'Administration',
          icon: 'pi pi-fw pi-lock',
          items: [
            {
              label: 'Manage Authorizers',
              icon: 'pi pi-fw pi-users',
              routerLink: ['/authorization-requests/administration/authorizers']
            },
            {
              label: 'Manage Request Types',
              icon: 'pi pi-fw pi-list',
              routerLink: ['/authorization-requests/administration/request-types']
            },
            {
              label: 'All Requests',
              icon: 'pi pi-fw pi-list',
              routerLink: ['/authorization-requests/administration/authorizer-admin-panel']
            },
            {
              label: 'Reason Code KPI',
              icon: 'pi pi-fw pi-chart-bar',
              routerLink: ['/authorization-requests/administration/reason-code-kpi']
            }
          ]
        });
      }

      restOfMenu.push(authorizationRequestsMenu);
    } else if (this.pageModeState === 'Planification') {
      restOfMenu.push({ label: 'Planification', items: [] });
    } else {
      restOfMenu.push({
        label: `Welcome ${this.usuario.nombre ? this.usuario.nombre : ''} ${
          this.usuario.apellido1 ? this.usuario.apellido1 : ''
        }`,
        items: visibleToolModules(this.usuario).map(tool => ({
          label: tool.title, icon: tool.icon, routerLink: [tool.home]
        }))
      });
    }

    const tool = TOOL_MODULES.find(item => item.mode === this.pageModeState);
    if (tool) {
      // Un único Home local; el logo es el acceso al dashboard general.
      const group = restOfMenu[0] ?? { label: tool.title, items: [] };
      group.label = tool.title;
      group.items = (group.items ?? []).filter(item => item.label !== 'Home' &&
        !(tool.mode === 'Test tracking' && item.label === 'Projects'));
      group.items.unshift({ label: 'Home', icon: 'pi pi-fw pi-home',
        routerLink: [tool.home], routerLinkActiveOptions: { exact: true } });
      const manual = USER_MANUALS[tool.mode];
      if (manual) group.items.push({ label: 'User manual', icon: 'pi pi-fw pi-book',
        url: manual, target: '_blank', title: 'Open user manual in a new tab' });
      restOfMenu = [group];
    }

    modelClone = modelClone.concat(restOfMenu);

    // console.log('model[0][label] => ', this.model[0]?.['label']);
    // console.log('modelClone[0][label] => ', modelClone[0]?.['label']);

    if (this.model[0]?.['label'] != modelClone[0]?.['label']) {
      this.model = modelClone;
    }

    console.log('model => ', this.model);
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
